import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
  Alert,
  Fab,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
  Dashboard as DashboardIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { formatDistance } from 'date-fns';

import { useCanvases, useDeleteCanvas } from '../../data/canvas/useCanvases';
import { Canvas } from '../../types/canvas.types';
import { PageTitle } from '../PageTitle';
import { BrowserTabTitle } from '../BrowserTabTitle';

interface CanvasCardProps {
  canvas: Canvas;
  onDelete: (canvasId: string) => void;
  onEdit: (canvas: Canvas) => void;
}

const CanvasCard: React.FC<CanvasCardProps> = ({
  canvas,
  onDelete,
  onEdit,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { accountId } = useParams();
  const navigate = useNavigate();

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    handleMenuClose();
    onEdit(canvas);
  };

  const handleDelete = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    handleMenuClose();
    if (window.confirm(`Are you sure you want to delete "${canvas.name}"?`)) {
      onDelete(canvas.id);
    }
  };

  const handleDuplicate = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    handleMenuClose();
    // TODO: Implement canvas duplication
    console.log('Duplicate canvas:', canvas.id);
  };

  const handleCardClick = () => {
    navigate(`/${accountId}/canvases/${canvas.slug}`);
  };

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 4,
        },
      }}
      onClick={handleCardClick}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          mb={2}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <DashboardIcon color="primary" />
            <Typography variant="h6" component="h2" noWrap>
              {canvas.name}
            </Typography>
          </Box>
          <IconButton size="small" onClick={handleMenuOpen} sx={{ ml: 1 }}>
            <MoreVertIcon />
          </IconButton>
        </Box>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 2, minHeight: '40px' }}
        >
          {canvas.settings?.['description'] || 'No description provided'}
        </Typography>

        <Typography variant="caption" color="text.secondary">
          Created{' '}
          {formatDistance(new Date(canvas.created_at), new Date(), {
            addSuffix: true,
          })}
        </Typography>
      </CardContent>

      <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
        <Typography variant="caption" color="text.secondary">
          /{canvas.slug}
        </Typography>
        <Button
          size="small"
          component={Link}
          to={`/${accountId}/canvases/${canvas.slug}`}
          onClick={(e) => e.stopPropagation()}
        >
          Open
        </Button>
      </CardActions>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem onClick={handleEdit}>
          <EditIcon sx={{ mr: 1 }} fontSize="small" />
          Edit
        </MenuItem>
        <MenuItem onClick={handleDuplicate}>
          <CopyIcon sx={{ mr: 1 }} fontSize="small" />
          Duplicate
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
          Delete
        </MenuItem>
      </Menu>
    </Card>
  );
};

export const CanvasList: React.FC = () => {
  const { accountId } = useParams();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');

  const { data: canvases = [], isLoading, error } = useCanvases(accountId);

  const deleteCanvasMutation = useDeleteCanvas(accountId);

  const handleCreateCanvas = () => {
    navigate(`/${accountId}/canvases/create`);
  };

  const handleDeleteCanvas = (canvasId: string) => {
    deleteCanvasMutation.mutate(canvasId);
  };

  const handleEditCanvas = (canvas: Canvas) => {
    // TODO: Implement canvas editing modal or navigate to edit page
    console.log('Edit canvas:', canvas);
  };

  // Filter canvases based on search term
  const filteredCanvases = canvases.filter(
    (canvas) =>
      canvas.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      canvas.slug.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Error loading canvases:{' '}
          {error instanceof Error ? error.message : 'Unknown error'}
        </Alert>
      </Box>
    );
  }

  return (
    <>
      <BrowserTabTitle>Canvases</BrowserTabTitle>
      <PageTitle text="Canvases" />

      <Box p={3}>
        {/* Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Typography variant="h4" component="h1">
            Canvases
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateCanvas}
            size="large"
          >
            Create Canvas
          </Button>
        </Box>

        {/* Search */}
        <Box mb={3}>
          <TextField
            fullWidth
            placeholder="Search canvases..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ maxWidth: 400 }}
          />
        </Box>

        {/* Canvas Grid */}
        {filteredCanvases.length === 0 ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            minHeight="400px"
            textAlign="center"
          >
            <DashboardIcon
              sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }}
            />
            <Typography variant="h5" color="text.secondary" gutterBottom>
              {searchTerm ? 'No canvases found' : 'No canvases yet'}
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              {searchTerm
                ? `No canvases match "${searchTerm}"`
                : 'Create your first canvas to get started with building interactive dashboards.'}
            </Typography>
            {!searchTerm && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateCanvas}
                size="large"
              >
                Create Your First Canvas
              </Button>
            )}
          </Box>
        ) : (
          <Grid container spacing={3}>
            {filteredCanvases.map((canvas) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={canvas.id}>
                <CanvasCard
                  canvas={canvas}
                  onDelete={handleDeleteCanvas}
                  onEdit={handleEditCanvas}
                />
              </Grid>
            ))}
          </Grid>
        )}

        {/* Floating Action Button for mobile */}
        <Fab
          color="primary"
          aria-label="create canvas"
          onClick={handleCreateCanvas}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            display: { xs: 'flex', md: 'none' },
          }}
        >
          <AddIcon />
        </Fab>
      </Box>
    </>
  );
};
